/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  NgZone,
  ViewEncapsulation,
  WritableSignal,
  computed,
  effect,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { LearnerFormAnswer, LearnerFormAnswerRequest, LearnerResourceProgress } from '@app/app/page-modules/resource/models';
import { LEARNER_VIEW_DATA_SERVICE, LearnerViewDataService } from '@app/app/page-modules/resource/services/data.service';
import { RefreshStateAfterFormSubmission } from '@app/app/page-modules/resource/store/learner-view.actions';
import { LearnerViewState } from '@app/app/page-modules/resource/store/learner-view.state';
import { ContentHelper } from '@app/app/shared/helpers/content-helper';
import { FileUploadHelper } from '@app/app/shared/helpers/file-upload-helper';
import { LanguageCodeHelper } from '@app/app/shared/helpers/language-code-helper';
import { SnackbarHelper } from '@app/app/shared/helpers/snackbar-helper';
import { FileAnswer, FileUploadBoxFormContent, Form, UserPlaylistSubmissionSummary } from '@app/app/shared/models';
import { FileUploadService, RESOURCES_FILE_UPLOAD_DATA_SERVICE } from '@app/app/shared/services/file-upload/file-upload.service';
import { Result } from '@app/app/shared/store';
import { linkedSignal } from '@app/app/signal-backports/linked_signal';
import { UserAuthState } from '@app/app/user-auth/store/user-auth.state';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { TranslocoService } from '@ngneat/transloco';
import { Store } from '@ngxs/store';
import { filter } from 'rxjs/operators';
import { SubmitPlaylistReviewDialogComponent } from 'src/app/page-modules/playlist/shared/view/playlist-action-bar/submit-dialog/submit-dialog.component';
import { RefreshUserPlaylistSubmissionSummary } from 'src/app/page-modules/playlist/store/view/playlist-view.state.actions';
import { DialogService } from 'src/app/shared/helpers/dialog/dialog.service';
import { v4 as uuidv4 } from 'uuid';

@Component({
  selector: 'ptl-form-preview-file-upload',
  templateUrl: './form-preview-file-upload.component.html',
  styleUrls: ['./form-preview-file-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class FormPreviewFileUploadComponent {
  formData = input<Form<FileUploadBoxFormContent>>();
  learnerFormAnswer = input<LearnerFormAnswer>();
  userPlaylistSubmissionSummary = input<UserPlaylistSubmissionSummary>();
  isProjectResource = input<boolean>();
  hideTextFormButtons = input<boolean>();
  playlistUri = input<string>();
  playlistUid = input<string>();
  playlistTitle = input<string>();
  resourceUri = input<string>();
  resourceUid = input<string>();
  publisherUri = input<string>();
  packageUri = input<string>();
  pageUri = input<string>();
  languageCode = input<string>();

  private resourceData = toSignal(this.store.select(LearnerViewState.resourceData).pipe(filter((resourceData) => !!resourceData)));
  protected userDetails = toSignal(this.store.select(UserAuthState.userDetailsData).pipe(filter(Boolean)));

  protected filesHovered = signal(false);
  protected fileUploaded = signal(false);
  protected fileUploadInProgress = signal<boolean>(false);
  protected fileRemoveInProgress = signal<boolean>(false);
  protected submitted = signal(false);
  protected canEdit = signal(false);
  protected canNotEditReason = signal<string>('');
  protected supportedFileTypes = signal<string[]>([]);
  protected requiresResubmission = signal(false);
  protected submissionInProgress = signal(false);
  protected validationInProgress = signal(false);
  protected validationProcessed = signal(false);
  protected answersStructure = signal<FileAnswer[]>([]);
  protected openAnnotationDocumentPath: WritableSignal<string | undefined> = linkedSignal({
    source: () => this.answersStructure(),
    computation: (files, previous) => {
      if (!previous?.value || !files.some(({ isPdf, pdfPreview, path }) => (isPdf || !!pdfPreview) && path === previous.value)) {
        return files.find((file) => file.isPdf || !!file.pdfPreview)?.path;
      } else {
        return previous?.value;
      }
    },
  });

  private cardUid = computed(() => this.resourceData()?._id || '');
  protected hasAnyPdfOrPreview = computed(() => this.answersStructure()?.some((file) => file.isPdf || !!file.pdfPreview));
  protected isSubmissionDisabled = computed(() => this.answersStructure().length < 1 || this.submissionInProgress());

  private fileInputEl = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  private readonly oneMegabyte = 1048576;

  constructor(
    private store: Store,
    private snackBar: MatSnackBar,
    private ngZone: NgZone,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private translocoService: TranslocoService,
    @Inject(LEARNER_VIEW_DATA_SERVICE) private dataService: LearnerViewDataService,
    @Inject(RESOURCES_FILE_UPLOAD_DATA_SERVICE) private fileUploadService: FileUploadService,
  ) {
    effect(() => {
      const formData = this.formData();
      untracked(() => {
        if (formData?.content) {
          const content = formData.content as FileUploadBoxFormContent;
          if (content.userAnswer) {
            this.submitted.set(content.submitted);
            this.fileUploaded.set(true);
            this.answersStructure.set(FileUploadHelper.formatAnswersStructure(content.userAnswer, content.filePreviews));
          }
          if (content.supportedFileTypes) {
            this.supportedFileTypes.set(content.supportedFileTypes);
          }
        }
      });
    });

    effect(() => {
      if (
        (this.playlistUid() && this.resourceUid() && this.formData()?.uid) ||
        this.learnerFormAnswer() ||
        this.userPlaylistSubmissionSummary()
      ) {
        untracked(() => {
          this.checkSubmissionMode();
        });
      }
    });
  }

  submit() {
    const submissionSummary = this.userPlaylistSubmissionSummary();
    if (ContentHelper.isProgressiveSubmissionMode(submissionSummary)) {
      const dialogMessage =
        submissionSummary?.userReviewSummary?.reviewType && submissionSummary.userReviewSummary.reviewType !== 'AUTOMATIC'
          ? this.translocoService.translate('translations.dialog.title.formSubmissionReviewEnabled')
          : this.translocoService.translate('translations.dialog.title.formSubmission');
      this.dialogService.showConfirmDialog(dialogMessage, this.translocoService).then((confirmed) => {
        if (confirmed) {
          this.processSubmit();
        }
      });
    } else {
      this.processSubmit();
    }
  }

  processSubmit() {
    if (ContentHelper.shouldDisplayReviewersDialogOnFormSubmission(this.userPlaylistSubmissionSummary())) {
      this.openReviewersDialog();
    } else {
      this.submitFileUpload();
    }
  }

  submitFileUpload(): void {
    this.submissionInProgress.set(true);
    this.dataService.submitForm(this.playlistUid(), this.resourceUid(), this.formData().uid).subscribe(({ isSuccess }) => {
      if (isSuccess) {
        this.refreshStateAndFinishSubmission();
      }
    });
  }

  private refreshStateAndFinishSubmission() {
    this.store.dispatch(new RefreshUserPlaylistSubmissionSummary(this.playlistUid()));
    this.dataService
      .getCardWithProgress(
        this.isProjectResource(),
        this.playlistUri(),
        this.resourceUri(),
        this.publisherUri(),
        this.packageUri(),
        this.pageUri(),
        this.languageCode(),
      )
      .subscribe(({ isSuccess, value }) => {
        if (isSuccess) {
          this.store.dispatch(new RefreshStateAfterFormSubmission(value));
          this.finishSubmission();
        }
      });
  }

  private openReviewersDialog() {
    const dialogRef = this.dialog.open(SubmitPlaylistReviewDialogComponent, {
      width: '90vw',
      minWidth: '15.625rem',
      maxWidth: '46.875rem',
      maxHeight: '33.75rem',
      restoreFocus: true,
      position: {
        top: '10vh',
      },
      direction: LanguageCodeHelper.getBodyLanguageDir(),
      panelClass: 'ptl-mat-dialog',
      backdropClass: 'dialog-backdrop',
      data: {
        isProjectResource: this.isProjectResource(),
        playlistUid: this.playlistUid(),
        playlistTitle: this.playlistTitle(),
        userPlaylistSubmissionSummary: this.userPlaylistSubmissionSummary(),
        mode: 'FORM',
        resourceUid: this.resourceUid(),
        formUid: this.formData().uid,
        playlistUri: this.playlistUri(),
        resourceUri: this.resourceUri(),
        publisherUri: this.publisherUri(),
        packageUri: this.packageUri(),
        pageUri: this.pageUri(),
        languageCode: this.languageCode(),
      },
    });
    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        this.finishSubmission();
      }
    });
  }

  private finishSubmission() {
    this.submissionInProgress.set(false);
    this.submitted.set(true);
    this.checkSubmissionMode();
  }

  async confirmRemoveFile(event: Event, filePath: string, index: number, filename: string) {
    event.preventDefault();
    event.stopPropagation();

    const message = this.translocoService.translate('translations.dialog.title.removeFramework').replace('{name}', filename);
    const element = document.createElement('div');
    element.innerHTML = message;

    if (await this.dialogService.showConfirmDialog(element, this.translocoService)) {
      this.removeFile(filePath, index);
    }
  }

  private removeFile(filePath: string, index: number): void {
    this.fileUploadInProgress.set(true);
    this.fileRemoveInProgress.set(true);
    this.dataService
      .removeFileFromForm(this.playlistUid(), this.cardUid(), this.formData().uid, filePath)
      .subscribe(({ isSuccess, error }) => {
        if (isSuccess) {
          const fileInputEl = this.fileInputEl();
          if (fileInputEl) {
            (fileInputEl.nativeElement as HTMLInputElement).value = '';
          }
          this.submitted.set(false);
          const currentAnswers = [...this.answersStructure()];
          currentAnswers.splice(index, 1);
          this.answersStructure.set(currentAnswers);

          if (currentAnswers.length === 0) {
            this.fileUploaded.set(false);
          }

          SnackbarHelper.showTranslatableSnackBar(
            this.ngZone,
            this.snackBar,
            this.translocoService,
            marker('translations.fileSuccessfullyRemoved'),
          );
        } else {
          SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, error);
        }
        this.fileUploadInProgress.set(false);
        this.fileRemoveInProgress.set(false);
      });
  }

  onFilesDropped(fileList: FileList): void {
    this.filesHovered.set(false);
    if (fileList.length === 1) {
      this.tryToSaveAnswer(fileList.item(0));
    } else {
      SnackbarHelper.showTranslatableSnackBar(this.ngZone, this.snackBar, this.translocoService, 'translations.uploadFileCountLimitError');
    }
  }

  onFileChanged(eventData: Event): void {
    this.tryToSaveAnswer((eventData.target as HTMLInputElement).files[0]);
  }

  getSupportedFileTypes(): string {
    return this.supportedFileTypes().join(', ').replace(/\./g, '');
  }

  triggerUpload() {
    const fileInputEl = this.fileInputEl();
    if (fileInputEl?.nativeElement) {
      fileInputEl.nativeElement.click();
    }
  }

  private tryToSaveAnswer(file: File): void {
    if (file) {
      // falsy when cancel is clicked
      const formData = this.formData();
      if (file.size > this.oneMegabyte * formData.content.maxFileSizeMb) {
        const translation = this.translocoService.translate('translations.uploadFileLimitError');
        const message = translation.replace('{maxFileSize}', formData.content.maxFileSizeMb.toString());
        SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, message);
        const fileInputEl = this.fileInputEl();
        if (fileInputEl) {
          (fileInputEl.nativeElement as HTMLInputElement).value = '';
        }
        return;
      }
      const fileExtension = file.name.split('.').splice(-1);
      if (!this.supportedFileTypes().includes(`.${fileExtension[0].toLowerCase()}`)) {
        SnackbarHelper.showTranslatableSnackBar(
          this.ngZone,
          this.snackBar,
          this.translocoService,
          marker('translations.uploadFileTypeError'),
        );
        const fileInputEl = this.fileInputEl();
        if (fileInputEl) {
          (fileInputEl.nativeElement as HTMLInputElement).value = '';
        }
        return;
      }
      this.uploadFileAndSaveAnswer(file);
    }
  }

  private uploadFileAndSaveAnswer(file: File): void {
    this.fileUploaded.set(true);
    this.fileUploadInProgress.set(true);
    const learnerAnswer: LearnerFormAnswerRequest = {
      formUid: this.formData().uid,
      answer: FileUploadHelper.formatFileNameForUpload(file.name),
    };
    this.dataService.saveFormAnswer(this.playlistUid(), this.cardUid(), learnerAnswer).subscribe(({ isSuccess, value }) => {
      if (isSuccess) {
        this.uploadFile(file, value).then(() => this.handleSaveAnswerSuccess(value));
      }
    });
  }

  private uploadFile(file: File, progressFromBackend: LearnerResourceProgress): Promise<Result<void>> {
    const uuidv4Id = uuidv4();
    const formData = this.formData();
    formData.content['uuid4'] = uuidv4Id;
    const currentForm = progressFromBackend.learnerFormAnswers.find((form) => form.formUid === formData.uid);
    const fullUrl = currentForm.answer.split(';');
    const filePath = FileUploadHelper.filePath(fullUrl[fullUrl.length - 1]);
    return this.fileUploadService.uploadFile(filePath, file, uuidv4Id).toPromise();
  }

  private handleSaveAnswerSuccess(progress: LearnerResourceProgress) {
    const currentForm = progress.learnerFormAnswers.find((form) => form.formUid === this.formData().uid);
    this.answersStructure.set(FileUploadHelper.formatAnswersStructure(currentForm.answer, currentForm.filePreviews));
    this.fileUploadInProgress.set(false);
  }

  private checkSubmissionMode(): void {
    const learnerFormAnswer = this.learnerFormAnswer();
    if (!learnerFormAnswer) {
      const playlistUid = this.playlistUid();
      const resourceUid = this.resourceUid();
      const formData = this.formData();
      if (!playlistUid || !resourceUid || !formData?.uid) {
        return;
      }
      if (!this.validationInProgress() && !this.validationProcessed()) {
        this.validationInProgress.set(true);
        this.dataService.validateFormUpdate(playlistUid, resourceUid, formData?.uid).subscribe(({ isSuccess, value }) => {
          if (isSuccess) {
            this.canEdit.set(value.canBeUpdated);
            this.canNotEditReason.set(ContentHelper.formCanNotBeEditedReason(this.translocoService, value));
            this.requiresResubmission.set(false);
            this.validationProcessed.set(true);
          }
          this.checkSubmissionLocked();
          this.validationInProgress.set(false);
        });
      }
    } else {
      this.canEdit.set(learnerFormAnswer?.updatePermission?.canBeUpdated);
      this.canNotEditReason.set(ContentHelper.formCanNotBeEditedReason(this.translocoService, learnerFormAnswer?.updatePermission));
      this.requiresResubmission.set(learnerFormAnswer?.requiresResubmission);
      this.checkSubmissionLocked();
    }
  }

  private checkSubmissionLocked() {
    if (this.userPlaylistSubmissionSummary()?.submissionsLocked) {
      this.canEdit.set(false);
      this.canNotEditReason.set(this.translocoService.translate('translations.formUpdateDisabledReasons.submissionClosed'));
      this.requiresResubmission.set(false);
    }
  }

  toggleAnnotationEditor(path: string): void {
    if (this.openAnnotationDocumentPath() === path) {
      this.openAnnotationDocumentPath.set(null);
    } else {
      this.openAnnotationDocumentPath.set(path);
    }
  }
}
